/**
 * @changelog
 *
 * 2026-09-14 (0.1.0) (jhoward)
 *   - Add useMaintenanceWindow(), which owns fetching, anchoring, ticking and dismissal for the scheduled
 *     maintenance warning banner.
 */

import type { FetchResponse } from '@framework/defs/framework';
import { useDocumentVisibility, useIntervalFn } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import type { MaintenanceWindowPayload } from '@/defs/MaintenanceWindow';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import useAppStore from '@/store';
import { status as maintenanceWindowStatus } from '@rss/routes/maintenance-window';

interface MaintenanceWindowResponse extends FetchResponse {
  data?: {
    maintenance_window: MaintenanceWindowPayload | null;
  };
}

export type MaintenanceWindowPhase = 'hidden' | 'dismissible' | 'sticky' | 'started';

/**
 * One key holding one window id, rather than a list.
 *
 * It scopes the dismissal to the window it was made against and self-cleans: the next window has a different id, so
 * the stored value simply stops matching and the banner comes back. A list would grow forever and still need
 * pruning. localStorage rather than sessionStorage so "I have seen this" survives opening a second tab.
 */
const DISMISS_KEY = 'opc.maintenance_window_dismissed';

/**
 * How often the window is re-fetched. Slower when there is nothing scheduled, because the worst case is that a
 * window becomes announceable up to five minutes into a lead time that is an hour long.
 */
const POLL_IDLE_MS = 300000;
const POLL_ACTIVE_MS = 60000;

/**
 * Safari in private mode throws on localStorage rather than failing quietly, and strict site-data settings can block
 * it outright - so every access is guarded and falls back to memory, which keeps the dismiss button working for the
 * life of the tab even when nothing can be persisted.
 */
const inMemoryDismissal = ref<string | null>(null);

function readDismissedId(): string | null {
  try {
    return window.localStorage.getItem(DISMISS_KEY);
  } catch {
    return inMemoryDismissal.value;
  }
}

function writeDismissedId(id: string): void {
  inMemoryDismissal.value = id;
  try {
    window.localStorage.setItem(DISMISS_KEY, id);
  } catch {
    //Nothing to do: inMemoryDismissal above already carries it for this tab.
  }
}

export default function useMaintenanceWindow() {
  const appStore = useAppStore();
  const visibility = useDocumentVisibility();

  const nowMs = ref<number>(performance.now());
  const dismissedId = ref<string | null>(readDismissedId());

  const window_ = computed<MaintenanceWindowPayload | null>(() => appStore.maintenanceWindow);

  /**
   * Seconds until the window opens, recomputed from the anchor rather than decremented.
   *
   * That is what makes a tab whose timers were throttled in the background correct the moment it is foregrounded,
   * and what keeps the guest's own device clock out of the calculation entirely - the server said how far away the
   * window was, and performance.now() says how long ago it said it.
   */
  const remainingSeconds = computed<number | null>(() => {
    const payload = window_.value;
    const anchor = appStore.maintenanceWindowAnchorMs;
    if (!payload || null === anchor) {
      return null;
    }

    return Math.round(payload.seconds_until_start - (nowMs.value - anchor) / 1000);
  });

  const secondsUntilEnd = computed<number | null>(() => {
    const payload = window_.value;
    const anchor = appStore.maintenanceWindowAnchorMs;
    if (!payload || null === anchor) {
      return null;
    }

    return Math.round(payload.seconds_until_end - (nowMs.value - anchor) / 1000);
  });

  const isDismissed = computed<boolean>(() => !!window_.value && dismissedId.value === String(window_.value.id));

  const phase = computed<MaintenanceWindowPhase>(() => {
    const payload = window_.value;
    const remaining = remainingSeconds.value;
    if (!payload || null === remaining) {
      return 'hidden';
    }

    //The window has run its course. The server stops sending it, but a tab that has not polled since should not sit
    //there showing a stale warning either.
    if (null !== secondsUntilEnd.value && secondsUntilEnd.value <= 0) {
      return 'hidden';
    }

    if (remaining <= 0) {
      return 'started';
    }

    if (remaining <= payload.sticky_within_seconds) {
      //Dismissal is deliberately not consulted here. Inside the threshold the warning comes back whether or not it
      //was dismissed an hour ago - that is the escalation.
      return 'sticky';
    }

    if (remaining > payload.lead_seconds) {
      return 'hidden';
    }

    return isDismissed.value ? 'hidden' : 'dismissible';
  });

  const isVisible = computed<boolean>(() => 'hidden' !== phase.value);
  const isSticky = computed<boolean>(() => 'sticky' === phase.value || 'started' === phase.value);

  function dismiss(): void {
    if (!window_.value) {
      return;
    }
    const id = String(window_.value.id);
    writeDismissedId(id);
    dismissedId.value = id;
  }

  async function fetchWindow(): Promise<void> {
    try {
      /*
       * The store is passed explicitly rather than left to the proxy's default parameter.
       *
       * That default is `useAppStore()`, evaluated at call time - and this runs from an interval callback and from
       * a visibility watcher, neither of which has a component instance current. Pinia then reaches for inject()
       * with no injection context, which logs "inject() can only be used inside setup()" on every poll. It happens
       * to resolve via the active pinia anyway, but a warning per minute per tab is not something to ship.
       */
      const response = await peppermillCustomFetchProxy<MaintenanceWindowResponse>(
        {},
        { route: maintenanceWindowStatus(), showLoadingSpinner: false },
        appStore as unknown as Parameters<typeof peppermillCustomFetchProxy>[2],
      );

      //Anything but a clean answer leaves the last known payload in place. Every fetch fails once the application
      //is actually down, and clearing on failure would drop the banner at precisely the wrong moment; a 429 from a
      //shared address behind NAT would do the same.
      if (200 !== response.status || !response.data) {
        return;
      }

      appStore.maintenanceWindow = response.data.maintenance_window ?? null;
      appStore.maintenanceWindowAnchorMs = performance.now();
    } catch {
      //Same reasoning as above - a thrown fetch is a failure to learn anything, not news that the window is gone.
    }
  }

  //A second while the countdown is on screen, half a minute otherwise: per-second precision buys nothing for
  //"begins in about 45 minutes", and this runs in every open tab.
  const tickInterval = computed<number>(() => (isSticky.value ? 1000 : 30000));

  const { pause: pauseTick, resume: resumeTick } = useIntervalFn(
    () => {
      nowMs.value = performance.now();
    },
    tickInterval,
    { immediate: true },
  );

  const pollInterval = computed<number>(() => (window_.value ? POLL_ACTIVE_MS : POLL_IDLE_MS));

  const { pause: pausePoll, resume: resumePoll } = useIntervalFn(() => void fetchWindow(), pollInterval, {
    immediate: true,
    immediateCallback: false,
  });

  watch(visibility, (current, previous) => {
    if ('visible' === current) {
      //Catch up at once rather than waiting out the interval: the clock has moved on, and a window may have been
      //cancelled while this tab was asleep.
      nowMs.value = performance.now();
      resumeTick();
      resumePoll();
      void fetchWindow();
    } else if ('visible' === previous) {
      //A hidden tab has nobody to warn, and forty of them share one per-IP throttle bucket.
      pauseTick();
      pausePoll();
    }
  });

  /**
   * Dismissing in one tab should hide it in the others, so the guest is not closing the same banner repeatedly.
   */
  function handleStorage(event: StorageEvent): void {
    if (DISMISS_KEY === event.key) {
      dismissedId.value = event.newValue;
    }
  }

  onMounted(() => {
    window.addEventListener('storage', handleStorage);

    //The watcher above only fires on a change, so a tab that was already in the background when this mounted would
    //never have been paused by it - it would poll and tick from a tab nobody is looking at, for the life of the
    //page. Restored on the first flip back to visible.
    if ('visible' !== document.visibilityState) {
      pauseTick();
      pausePoll();

      return;
    }

    //POST /state already seeds this on an ordinary page load. It cannot for a signed-out visitor - it sits behind
    //TwoFactorAuthenticationMiddleware and answers 401 - so an unseeded anchor is the signal to go and ask.
    if (null === appStore.maintenanceWindowAnchorMs) {
      void fetchWindow();
    }
  });

  onUnmounted(() => {
    window.removeEventListener('storage', handleStorage);
    pauseTick();
    pausePoll();
  });

  return {
    window: window_,
    phase,
    isVisible,
    isSticky,
    isDismissed,
    remainingSeconds,
    secondsUntilEnd,
    dismiss,
    fetchWindow,
  };
}
